import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, CheckCircle, Bell, AlertTriangle, CalendarClock, ShieldCheck, Zap, HeartHandshake, MessageSquare, Smartphone, Megaphone, PhoneCall } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const useCases = [
  {
    title: "OTP Authentication",
    description: "Verify logins, sign-ups, and high-value transactions with secure one-time passwords.",
    Icon: Lock,
    color: "text-amber",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Transaction Verification",
    description: "Confirm high-value or unusual transactions before they're processed.",
    Icon: CheckCircle,
    color: "text-lime",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Account Activity Notifications",
    description: "Alert customers to deposits, withdrawals, or balance changes as they happen.",
    Icon: Bell,
    color: "text-sky",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Fraud Alerts",
    description: "Notify customers immediately of suspicious account activity.",
    Icon: AlertTriangle,
    color: "text-rose",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Payment Reminders",
    description: "Send reminders ahead of payment due dates to reduce delinquency.",
    Icon: CalendarClock,
    color: "text-violet",
    tags: ["SMS"],
  },
];

const enhancedBenefits = [
  {
    title: "Stop Fraud Before It Costs You",
    description: "Real-time alerts help customers and teams catch suspicious activity early.",
    Icon: ShieldCheck,
    color: "text-amber",
  },
  {
    title: "Faster Verification",
    description: "Reduce OTP delivery time with direct carrier connections.",
    Icon: Zap,
    color: "text-sky",
  },
  {
    title: "Boost Customer Trust",
    description: "Timely, reliable communication builds confidence in your platform.",
    Icon: HeartHandshake,
    color: "text-lime",
  },
];

const supportedChannels = [
  { title: "SMS", description: "Deliver OTPs and alerts over the most universally reachable channel.", Icon: MessageSquare, gradient: "icon-tile-teal" },
  { title: "RCS", description: "Use interactive messaging for verification and notifications.", Icon: Megaphone, gradient: "icon-tile-indigo" },
  { title: "Voice", description: "Deliver time-sensitive alerts by automated voice call.", Icon: PhoneCall, gradient: "icon-tile-blue" },
];

const faqs = [
  { question: "What is fintech SMS messaging?", answer: "Fintech SMS messaging refers to text messages sent by banks, fintechs, and payment platforms to communicate with customers, including OTPs, transaction alerts, fraud warnings, and payment reminders. It's a fast, direct way to deliver time-sensitive financial information." },
  { question: "Is financial SMS messaging legal?", answer: "Yes, as long as it follows data protection and telecom regulations. Transactional messages like OTPs, balance updates, and fraud alerts are typically exempt from marketing opt-in requirements, while promotional texts still require prior consent." },
  { question: "How fast are OTPs and alerts delivered?", answer: "Because Yootelco connects directly to mobile networks, OTPs and alerts are typically delivered within seconds. Delivery speed can vary by destination country and channel." },
  { question: "Can Yootelco support KYC and compliance requirements?", answer: "Yootelco's OTP and verification messaging can support strong customer authentication and KYC workflows common in regulated fintech and banking environments." },
  { question: "What channels can fintechs use for customer messaging?", answer: "Fintechs can reach customers over SMS, RCS, and voice, choosing the right channel for OTPs, alerts, and account notifications." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const channelIcons = [
  { label: "SMS", Icon: MessageSquare },
  { label: "RCS", Icon: Megaphone },
  { label: "Voice", Icon: PhoneCall },
];

const IndustriesFintechPayments = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="SMS & OTP Messaging for Fintech & Payments – Yootelco"
        description="Secure OTP delivery, transaction alerts, and fraud-resistant messaging built for fintechs and payment platforms. Talk to Yootelco today."
        path="/industries/fintech-payments"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Built for Regulated Financial Services
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              Messaging for Fintech & Payments
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Secure OTP delivery and real-time transaction alerts, delivered through carrier-grade infrastructure. Yootelco helps fintechs and payment platforms verify users and keep customers informed, without compromising on speed or compliance.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {channelIcons.map((channel) => (
                <div key={channel.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <channel.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {channel.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Fintech & Payments Choose Yootelco */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Why Fintech & Payments Choose Yootelco</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            With more than 18 years of experience in telecommunications and digital communications, we help fintech companies, payment providers, and financial institutions deliver secure, reliable, and compliant customer communications. Whether it's transaction notifications, one-time passwords (OTPs), fraud alerts, account updates, or customer engagement campaigns, our solutions are built to meet the highest standards of performance and reliability.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            We understand the importance of speed, security, and compliance in the financial sector.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Use Cases of Fintech & Payments Messaging</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCases.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} className="mx-auto block w-fit" />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Enhanced Benefits */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Enhanced Benefits for Fintech & Payments</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {enhancedBenefits.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Stay Connected and Compliant */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Stay Connected and Compliant</h2>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "UK-based team, built for enterprise and regulated clients",
              "Multi-channel delivery across SMS, RCS, and Voice",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Channels */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Supported Channels</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {supportedChannels.map((channel) => {
            const Icon = channel.Icon;
            return (
              <article
                key={channel.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-8 text-center transition-all duration-300 hover:-translate-y-1"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${channel.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{channel.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{channel.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA Banner */}
            <CTASection
        title="Talk to Sales"
        description="Tell us your use case, we'll show you how Yootelco can support your fintech or payments messaging needs."
        useCase="Fintech & Payments"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-shell">
        <div className="mx-auto max-w-[800px] rounded-[36px] border border-border/70 bg-gradient-to-br from-secondary/60 to-card px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <h2 className="section-title">Ready to Secure Your Fintech Messaging?</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
            Talk to our team about OTP delivery, transaction alerts, and fraud prevention for your platform.
          </p>
          <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default IndustriesFintechPayments;
